import {
  DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector
} from "@mui/x-data-grid";
import LinearProgress from '@mui/material/LinearProgress';
import Box from "@mui/material/Box";
import Button from '@mui/material/Button';
import SettingsIcon from '@mui/icons-material/Settings';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import DownloadSharpIcon from '@mui/icons-material/DownloadSharp';
import InfoIcon from '@mui/icons-material/Info';
import { GetFullDataFile } from "../API/TrackRecord";
import { useEffect, useState, useMemo } from "react";
import { getConfig } from "../config";
import { CASH_POSITIONS } from "../utils/constants";
import ColumnHeaderFilterButton from "./ColumnHeaderFilterButton";

let appConfig = await getConfig();

function ViewTrackRecord(props) {
const { 
  query, 
  quickQuery, 
  rows, 
  columns, 
  loader, 
  correlationIdTracker, 
  rowCount, 
  pnlTip, 
  expoTip, 
  aggregate, 
  appBusinessUnit,
  isCashflow,
  isAdvanced
} = props;
const [rowMessage, setRowMessage] = useState("")
const [impliedCashMessage, setImpliedCashMessage] = useState("")
const [searchTypeMessage, setSearchTypeMessage] = useState("")
const [paginationModel, setPaginationModel] = useState(JSON.parse(localStorage.getItem('paginationModel')) || {
  pageSize: 10
});

function createTooltips(cols) {
  const isPnl = cols.field.startsWith("Profit/Loss") || cols.field === "Adjusted PNL (Book, DTD)";
  const isExpo = cols.field.startsWith("Exposure") || cols.field === "Adjusted Exposure (Book, BOD)";
  if (isPnl && pnlTip !== null) {
    return pnlTip;
  } else if (isExpo && expoTip !== null) {
    return expoTip;
  }
  return cols.headerName;
}

useEffect(() => {
  localStorage.setItem('paginationModel', JSON.stringify(paginationModel));
}, [paginationModel]);

useEffect(() => {
  if (isAdvanced) {
    setSearchTypeMessage(
      <div style={{ display: 'flex', alignItems: 'center' }}>
      <InfoIcon style={{ color:"skyblue" }}/>
      <span style={{ marginLeft: '4px', backgroundColor: "powderblue" }}> 
        Data Generated with Advanced Search</span>
      </div>
    );
  } else if (!isAdvanced) {
      setSearchTypeMessage(
        <div style={{ display: 'flex', alignItems: 'center' }}>
        <InfoIcon style={{ color:"skyblue" }}/>
        <span style={{ marginLeft: '4px', backgroundColor: "powderblue" }}> 
          Data Generated with Quick Search</span>
        </div>
      );
    } else {
      setSearchTypeMessage(null);
    }

  if (rowCount > 10000) {
    setRowMessage(
      <div style={{ display: 'flex', alignItems: 'center' }}>
      <InfoIcon style={{ color:"skyblue" }}/>
      <span style={{ marginLeft: '4px', backgroundColor: "powderblue" }}> 
        The first 10,000 rows are displayed. Click Export for the full dataset consisting of {rowCount.toLocaleString()} rows.</span>
      </div>
    );
  } else {
    setRowMessage(null)
  }
  const containsSecurityOrIssuer = rules => 
    rules.some(rule => rule.field.includes("Security") || rule.field.includes("Issuer"));  

  const fundAggregation = 
  aggregate.length > 0 &&
  aggregate.every((a) => CASH_POSITIONS.AGGREGATE_VALUES.includes(a.label)) &&
  (isAdvanced
    ? !containsSecurityOrIssuer(query.rules) 
    : !containsSecurityOrIssuer(quickQuery.rules)
  );

  if (appBusinessUnit && appBusinessUnit === "Credit" && fundAggregation) {
    setImpliedCashMessage(
      <div style={{ display: 'flex', alignItems: 'center', fontSize: '15px' }}>
        <InfoIcon style={{ color: "skyblue", fontSize: '20px' }} />
        <span style={{ marginLeft: '4px', backgroundColor: "powderblue" }}> 
          {CASH_POSITIONS.USER_ALERT}
        </span>
      </div>
    );
  }
  else {
    setImpliedCashMessage(null)
  }
}, [rowCount, aggregate, appBusinessUnit, isAdvanced, query.rules, quickQuery.rules]);

async function HandleCustomExport() {
    try {
    const zipContent = await GetFullDataFile(correlationIdTracker, isCashflow)
    // Create a URL for the ZIP blob
    const zipUrl = URL.createObjectURL(zipContent);

    // Create a link and simulate a click to trigger download
    const a = document.createElement('a');
    a.href = zipUrl;
    a.download = `${correlationIdTracker}.zip`;
    a.click();
    } catch (error) {
        console.error("Error downloading file:", error)
    }
}

const toolbarButtonSx = {
  backgroundColor: "#bbdefb",
  color: "#000270",
  textTransform: "none",
  borderRadius: "8px",
  fontWeight: 500,
  "&:hover": { backgroundColor: "#90caf9" },
};

const toolbarIconSx = { color: "#000270", fontSize: "1.2rem" };

const dataGridSlots = {
  columnSelectorIcon: () => <SettingsIcon sx={toolbarIconSx} />,
  openFilterButtonIcon: () => <FilterAltIcon sx={toolbarIconSx} />,
  densityCompactIcon: () => <ViewAgendaIcon sx={toolbarIconSx} />,
  densityStandardIcon: () => <ViewAgendaIcon sx={toolbarIconSx} />,
  densityComfortableIcon: () => <ViewAgendaIcon sx={toolbarIconSx} />,
  columnHeaderFilterIconButton: ColumnHeaderFilterButton,
};

const dataGridSlotProps = {
  baseButton: { sx: toolbarButtonSx },
};

const dataGridLocaleText = {
  toolbarColumns: "Columns",
  toolbarFilters: "Filters",
  toolbarDensity: "Density",
};

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ gap: 1, justifyContent: 'flex-end' }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Button
        variant="text"
        onClick={HandleCustomExport}
        startIcon={<DownloadSharpIcon sx={toolbarIconSx} />}
        size="small"
        sx={toolbarButtonSx}
      >
        Export as .xls
      </Button>
    </GridToolbarContainer>
  );
}

const columnVisibilityModel = useMemo(() => {
  if (!isCashflow) return undefined;
  
  return columns.reduce((acc, col) => {
    acc[col.field] = appConfig.CASHFLOW_INITIAL_COLUMNS.includes(col.field);
    return acc;
  }, {})
}, [columns, isCashflow]) ;

return loader 
  ? (
    <Box sx = {{paddingTop: '15px'}}>
      <LinearProgress />
    </Box>
  )
  : (
    <Box sx={{ height: "100%", width: "100%", paddingLeft: "10px", paddingRight: "10px"}}>
      <div> 
      {rowMessage}
      {impliedCashMessage}
      {searchTypeMessage}
      </div>
      <DataGrid
        density="compact"
        autoHeight
        rows={rows}
        columns={columns.map((col) => ({
          ...col,
          description: createTooltips(col),
        }))}
        getRowId={(row) => row.internalId}
        initialState={{
          pagination: {
            paginationModel: paginationModel
          },
          columns: { columnVisibilityModel },
        }}
        loading={loader}
        pageSizeOptions={[10, 25, 50, 75, 100]}
        checkboxSelection
        disableRowSelectionOnClick
        slots={{
          toolbar: CustomToolbar,
          loadingOverlay: LinearProgress,
          ...dataGridSlots,
        }}
        slotProps={dataGridSlotProps}
        localeText={dataGridLocaleText}
        onPaginationModelChange={setPaginationModel}
        sx={{
          border: "none",
          /* Header row: pill-shaped with rounded corners, border, and light background */
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f0f4fa",
            borderRadius: "9999px",
            border: "1px solid #b0bec5",
            minHeight: "44px !important",
            maxHeight: "44px !important",
            overflow: "hidden",
          },
          "& .MuiDataGrid-columnHeadersInner": {
            minHeight: "44px !important",
          },
          /* Individual column header cells */
          "& .MuiDataGrid-columnHeader": {
            color: "#000270",
            fontWeight: 600,
            fontSize: "0.85rem",
            borderRight: "1px solid #b0bec5",
            "&:last-of-type": {
              borderRight: "none",
            },
            "&:focus, &:focus-within": {
              outline: "none",
            },
          },
          /* Column separator lines hidden (dividers handled by borderRight above) */
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          /* Row styling */
          "& .MuiDataGrid-row": {
            borderBottom: "1px solid #e0e0e0",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            "&:focus, &:focus-within": {
              outline: "none",
            },
          },
          /* Remove the default bottom border on the column headers row */
          "& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader--sortable": {
            cursor: "pointer",
          },
        }}
      />
    </Box>
  );
}

export default ViewTrackRecord;
